/* eslint-disable prefer-destructuring */
/* eslint-disable no-unused-expressions */
import React, { useEffect, useState } from 'react';
import ShowChartIcon from '@material-ui/icons/ShowChart';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
//
import { EView } from 'components/deal-results/product-items';
import { formatNumber } from 'lib/helpers/currency';
import { mapNameToDisplayName } from 'lib/helpers/field-options';
import { IDealProduct, IFieldOption } from 'store/field-options/types';
import messages from 'constants/messages';
import { IDeal } from 'store/deals/types';
import {
  ISummaryProduct,
  EEvaluationUnit,
  ISummaryOption,
  ISummaryPrice,
  TNullableFloat,
} from 'store/deal-evaluation/types';
import labels from 'constants/labels';
import round from 'lib/helpers/round';
// prettier-ignore
import { Bottom, ChartedContainer, Container, DateRange, ExpandLessIcon, ExpandMoreIcon, ExpansionButton, ProductCell, ProductItemHeader, ProductName, StackedField, TableRow, Top, VisualToggleContainer } from 'components/deal-results/product-items/_common';
import { generateProductPeriodString } from 'lib/helpers/date-range';
import ProductItemItemizedContainer from 'components/deal-results/product-items/product-item-itemized-container';
import VisualToggleButton from 'components/buttons/visual-toggle';
import Visuals from 'components/visuals/container';
import BoxPlotBareIcon from 'components/icons/box-plot-bare';
import SurvivalIcon from 'components/icons/survival';
import colors from 'theme/colors';

const { notApplicable } = messages.info;

interface IProductItemProps {
  availableProducts: IFieldOption[] | undefined;
  deal: IDeal;
  loading: boolean;
  productSummary: ISummaryProduct;
  selectedUnitType: EEvaluationUnit;
  pricePerUnitLabel: string;
  id: string;
  index: number;
}

function getAve(component: ISummaryOption) {
  return component.ave ?? 0;
}

const checkDealProductNameAndIndex = (
  d: IDealProduct,
  p: IProductInterface,
  dealProductIndex: number,
  index: number
) => {
  return d.name === p.name && dealProductIndex === index;
};

const getDealProductVolumeUnit = (d: IDealProduct) => {
  return d.volumeUnit ?? notApplicable;
};

const getMainBatnaDescription = (d: IDealProduct) => {
  return d.mainBatna ? d.mainBatna.description : notApplicable;
};

const getMainBatnaFormula = (d: IDealProduct) => {
  return d.mainBatna ? d.mainBatna.formula : notApplicable;
};

const getDealProductCapValue = (d: IDealProduct) => {
  return d.capValue ?? notApplicable;
};

const getDealProductFloorValue = (d: IDealProduct) => {
  return d.floorValue ?? notApplicable;
};

const getBasePriceDescription = (d: IDealProduct) => {
  return d.basePrice ? d.basePrice.description : notApplicable;
};

const getBasePriceFormula = (d: IDealProduct) => {
  return d.basePrice ? d.basePrice.formula : notApplicable;
};

const getMainBatnaCostReimbursed = (d: IDealProduct) => {
  return d.mainBatna ? d.mainBatna.costReimbursed : 0;
};

const getMainBatnaCostFreight = (d: IDealProduct) => {
  return d.mainBatna ? d.mainBatna.costFreight : 0;
};

const getBasePriceCostReimbursed = (d: IDealProduct) => {
  return d.basePrice ? d.basePrice.costReimbursed : 0;
};

const getBasePriceCostFreight = (d: IDealProduct) => {
  return d.basePrice ? d.basePrice.costFreight : 0;
};

export interface IProductInterface {
  name: string;
  volumeUnit: string;
  startDate: string | Date | undefined;
  endDate: string | Date | undefined;
  floorValue: string;
  capValue: string;
  basePrice: {
    description: string;
    formula: string;
    costFreight: TNullableFloat;
    costReimbursed: TNullableFloat;
    costToServe: TNullableFloat;
  };
  mainBatna: {
    description: string;
    formula: string;
    costFreight: TNullableFloat;
    costReimbursed: TNullableFloat;
    costToServe: TNullableFloat;
  };
  otherBatnas: ISummaryPrice[];
}

const getMapEvaluationDealProductData = (
  productSummary: ISummaryProduct,
  contract: ISummaryPrice,
  mainBatna: ISummaryPrice,
  dealProducts: IDealProduct[] | undefined,
  index: number,
  otherBatnas: ISummaryPrice[]
) => {
  const p: IProductInterface = {
    name: productSummary.name,
    volumeUnit: '',
    startDate: '',
    endDate: '',
    floorValue: '',
    capValue: '',
    basePrice: {
      description: '',
      formula: '',
      costFreight: 0,
      costReimbursed: 0,
      costToServe: contract.costToServe,
    },
    mainBatna: {
      description: '',
      formula: '',
      costFreight: 0,
      costReimbursed: 0,
      costToServe: mainBatna.costToServe,
    },
    otherBatnas: [],
  };
  // Map evaluation data to deal product data
  dealProducts?.forEach((d: IDealProduct, dealProductIndex: number) => {
    if (checkDealProductNameAndIndex(d, p, dealProductIndex, index)) {
      p.volumeUnit = getDealProductVolumeUnit(d);
      p.startDate = d.startDate;
      p.endDate = d.endDate;
      p.mainBatna.description = getMainBatnaDescription(d);
      p.mainBatna.formula = getMainBatnaFormula(d);
      p.capValue = getDealProductCapValue(d);
      p.floorValue = getDealProductFloorValue(d);
      p.basePrice.description = getBasePriceDescription(d);
      p.basePrice.formula = getBasePriceFormula(d);
      p.mainBatna.costReimbursed = getMainBatnaCostReimbursed(d);
      p.mainBatna.costFreight = getMainBatnaCostFreight(d);
      p.basePrice.costReimbursed = getBasePriceCostReimbursed(d);
      p.basePrice.costFreight = getBasePriceCostFreight(d);

      // Map otherBatna data using index
      if (!d.otherBatnas || !otherBatnas) {
        return;
      }
      d.otherBatnas.forEach((otherBatna, otherBatnaIndex) => {
        const b = otherBatnas[otherBatnaIndex];
        p.otherBatnas[otherBatnaIndex] = {
          ...otherBatna,
          ...b,
        };
      });
    }
  });
  return p;
};

const getMainItemValue = (
  contract: ISummaryPrice,
  pricePerUnitLabel: string,
  volume: number,
  currencyMultiplier: number
) => {
  // Netback
  const contractNetbackPrice = contract.netback.total.ave;
  // Main item
  const contractPriceMainOutput = `${formatNumber(contractNetbackPrice, 0)} (${pricePerUnitLabel})`;
  let contractRevenueMainOutput = notApplicable;
  if (contractNetbackPrice !== null) {
    contractRevenueMainOutput = `${formatNumber(round(contractNetbackPrice * volume * currencyMultiplier), 0)} ($)`;
  }

  return {
    contractPriceMainOutput,
    contractRevenueMainOutput,
  };
};

const getProductSummaryOptionsValue = (
  productSummary: ISummaryProduct,
  p: IProductInterface,
  creditCost: TNullableFloat
) => {
  let capValue = null;
  let capValueOutput = '';
  let floorValue = null;
  let floorValueOutput = '';
  let pushValue = null;
  let pushValueOutput = '';
  let pullValue = null;
  let pullValueOutput = '';
  let extensionValue = null;
  let extensionValueOutput = '';
  let earlyTermValue = null;
  let earlyTermValueOutput = '';
  let costToServeOutput = '';
  let creditValueOutput = '';

  const { cap, floor, push, pull, extension, earlyTerm } = productSummary.options;

  // Cap
  if (cap !== null) {
    capValue = getAve(cap);
    capValueOutput = formatNumber(capValue, 0, true);
  }
  // Floor
  if (floor !== null) {
    floorValue = getAve(floor);
    floorValueOutput = formatNumber(floorValue, 0, true);
  }
  // Push
  if (push !== null) {
    pushValue = getAve(push);
    pushValueOutput = formatNumber(pushValue, 0, true);
  }
  // Pull
  if (pull !== null) {
    pullValue = getAve(pull);
    pullValueOutput = formatNumber(pullValue, 0, true);
  }
  // Extension
  if (extension !== null) {
    extensionValue = getAve(extension);
    extensionValueOutput = formatNumber(extensionValue, 0, true);
  }
  // Early Termination
  if (earlyTerm !== null) {
    earlyTermValue = getAve(earlyTerm);
    earlyTermValueOutput = formatNumber(earlyTermValue, 0, true);
  }

  // Costs
  if (p.basePrice.costToServe) {
    costToServeOutput = formatNumber(p.basePrice.costToServe, 0, true);
  } else {
    costToServeOutput = '0.00';
  }

  // Credit
  if (creditCost) {
    creditValueOutput = formatNumber(creditCost, 0, true);
  } else {
    creditValueOutput = '0.00';
  }
  return {
    floorValueOutput,
    capValueOutput,
    pullValueOutput,
    pushValueOutput,
    earlyTermValueOutput,
    extensionValueOutput,
    costToServeOutput,
    creditValueOutput,
  };
};

const getVolumeLabel = (selectedUnitType: EEvaluationUnit) => {
  return selectedUnitType === EEvaluationUnit.LBS ? labels.volume.pound : labels.volume.ton;
};

const UNIT_TYPE = 0.01;

const getCurrencyMultiplier = (selectedUnitType: EEvaluationUnit) => {
  return selectedUnitType === EEvaluationUnit.LBS ? UNIT_TYPE : 1;
};

const getBaseMarginPrice = (withoutOpts: ISummaryPrice) => {
  return withoutOpts.margin ? withoutOpts.margin.total.ave : null;
};

const getMainBatnaMarginPrice = (mainBatna: ISummaryPrice) => {
  return mainBatna.margin ? mainBatna.margin.total.ave : null;
};

const checkContractSalesPrice = (contractSalesPrice: TNullableFloat) => {
  return contractSalesPrice !== null;
};

const checkContractNetbackPrice = (contractNetbackPrice: TNullableFloat) => {
  return contractNetbackPrice !== null;
};

const handlePotentialErrors = (dealProducts: IDealProduct[] | undefined, volume: number) => {
  return !dealProducts || !volume;
};

export default ({
  availableProducts,
  deal,
  loading,
  pricePerUnitLabel,
  productSummary,
  selectedUnitType,
  id: productItemId,
  index,
}: IProductItemProps) => {
  const [expanded, setExpanded] = useState(true);
  const [view, setView] = useState(EView.ITEMIZED);
  const { products: dealProducts } = deal;
  const { contract, mainBatna, volume, creditCost, otherBatnas } = productSummary;
  // Handle potential errors
  if (handlePotentialErrors(dealProducts, volume)) {
    return (
      <TableRow>
        <ProductCell>{messages.errors.default}</ProductCell>
      </TableRow>
    );
  }

  // Map evaluation data to deal product data
  const p = getMapEvaluationDealProductData(productSummary, contract, mainBatna, dealProducts, index, otherBatnas);

  // Calculate date-related data
  const dateRange = generateProductPeriodString(p.startDate, p.endDate);

  // Labels
  const productName = mapNameToDisplayName(p.name, availableProducts) || notApplicable;
  const volumeLabel = getVolumeLabel(selectedUnitType);

  // Volume
  let volumeOutput = `${formatNumber(volume, 0)} ${volumeLabel}`;

  // Handle proper output of currency values
  const currencyMultiplier = getCurrencyMultiplier(selectedUnitType);

  /************/
  /* Contract */
  /************/

  // Revenue
  const contractSalesPrice = contract.sales.total.ave;
  let contractSalesPriceOutput = notApplicable;
  if (checkContractSalesPrice(contractSalesPrice)) {
    contractSalesPriceOutput = formatNumber(contractSalesPrice, 0, true);
  }

  // Netback
  const contractNetbackPrice = contract.netback.total.ave;
  let contractNetbackPriceOutput = notApplicable;
  if (checkContractNetbackPrice(contractNetbackPrice)) {
    contractNetbackPriceOutput = formatNumber(contractNetbackPrice, 0, true);
  }

  // Margin
  let contractMarginPrice = null;
  const { margin: contractMarginPriceFormula } = contract;
  let contractMarginPriceOutput = notApplicable;
  if (contractMarginPriceFormula !== null) {
    contractMarginPrice = contractMarginPriceFormula.total.ave;
    contractMarginPriceOutput = formatNumber(contractMarginPrice, 0, true);
  }

  let { contractPriceMainOutput, contractRevenueMainOutput } = getMainItemValue(
    contract,
    pricePerUnitLabel,
    volume,
    currencyMultiplier
  );

  /************************/
  /* Contract w/o options */
  /************************/

  // Contract without Options
  const { withoutOpts } = productSummary;
  let baseSalesPrice;
  let baseNetbackPrice;
  let baseMarginPrice;

  // base != contract
  if (withoutOpts) {
    baseSalesPrice = withoutOpts.sales.total.ave;
    baseNetbackPrice = withoutOpts.netback.total.ave;
    baseMarginPrice = getBaseMarginPrice(withoutOpts);
  } else {
    baseSalesPrice = contractSalesPrice;
    baseNetbackPrice = contractNetbackPrice;
    baseMarginPrice = contractMarginPrice;
  }

  // Revenue
  let baseSalesPriceOutput = formatNumber(baseSalesPrice, 0, true);

  // Netback
  let baseNetbackPriceOutput = formatNumber(baseNetbackPrice, 0, true);

  // Margin
  let baseMarginPriceOutput = formatNumber(baseMarginPrice, 0, true);

  /*********/
  /* BATNA */
  /*********/

  // Revenue
  const mainBatnaSalesPrice = mainBatna.sales.total.ave;
  let mainBatnaSalesPriceOutput = formatNumber(mainBatnaSalesPrice, 0, true);

  // Netback
  const mainBatnaNetbackPrice = mainBatna.netback.total.ave;
  let mainBatnaNetbackPriceOutput = formatNumber(mainBatnaNetbackPrice, 0, true);

  // Margin
  const mainBatnaMarginPrice = getMainBatnaMarginPrice(mainBatna);
  let mainBatnaMarginPriceOutput = formatNumber(mainBatnaMarginPrice, 0, true);

  /***********/
  /* Options */
  /***********/

  const {
    floorValueOutput,
    capValueOutput,
    pullValueOutput,
    pushValueOutput,
    earlyTermValueOutput,
    extensionValueOutput,
    costToServeOutput,
    creditValueOutput,
  } = getProductSummaryOptionsValue(productSummary, p, creditCost);

  // Handle loading state
  if (loading) {
    contractSalesPriceOutput = labels.general.ellipsis;
    contractNetbackPriceOutput = labels.general.ellipsis;
    contractMarginPriceOutput = labels.general.ellipsis;

    baseSalesPriceOutput = labels.general.ellipsis;
    baseNetbackPriceOutput = labels.general.ellipsis;
    baseMarginPriceOutput = labels.general.ellipsis;

    mainBatnaSalesPriceOutput = labels.general.ellipsis;
    mainBatnaNetbackPriceOutput = labels.general.ellipsis;
    mainBatnaMarginPriceOutput = labels.general.ellipsis;

    contractPriceMainOutput = labels.general.ellipsis;
    contractRevenueMainOutput = labels.general.ellipsis;
    volumeOutput = labels.general.ellipsis;
  }

  // Initialize expansion state (show if options exist)
  useEffect(() => {
    if (productSummary) {
      setExpanded(true);
    }
  }, [productSummary]);

  function handleViewTypeChange(newView: EView) {
    setView(newView);
  }

  return (
    <Container>
      <Top>
        <ProductName>{productName.toUpperCase()}</ProductName>
        <DateRange>{dateRange}</DateRange>
        <StackedField>
          <span>{volumeOutput}</span>
          <span>VOLUME</span>
        </StackedField>
        <StackedField>
          <span>{contractPriceMainOutput}</span>
          <span>NETBACK</span>
        </StackedField>
        <StackedField>
          <span>{contractRevenueMainOutput}</span>
          <span>NETBACK TOTAL</span>
        </StackedField>
        <ExpansionButton onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ExpansionButton>
      </Top>
      {expanded && (
        <Bottom>
          <ProductItemHeader>
            <VisualToggleContainer>
              <VisualToggleButton
                active={view === EView.ITEMIZED}
                icon={<FormatListBulletedIcon />}
                onClick={() => handleViewTypeChange(EView.ITEMIZED)}
              />
              <VisualToggleButton
                active={view === EView.LINE}
                icon={<ShowChartIcon />}
                onClick={() => handleViewTypeChange(EView.LINE)}
              />
              <VisualToggleButton
                active={view === EView.SURVIVAL}
                icon={<SurvivalIcon fill={view === EView.SURVIVAL ? colors.primary : colors.globalIcon} />}
                onClick={() => handleViewTypeChange(EView.SURVIVAL)}
              />
              <VisualToggleButton
                active={view === EView.BOX_PLOT}
                icon={<BoxPlotBareIcon fill={view === EView.BOX_PLOT ? colors.primary : colors.globalIcon} />}
                onClick={() => handleViewTypeChange(EView.BOX_PLOT)}
              />
            </VisualToggleContainer>
          </ProductItemHeader>

          {view === EView.ITEMIZED ? (
            <ProductItemItemizedContainer
              pricePerUnitLabel={pricePerUnitLabel}
              p={p}
              contractSalesPriceOutput={contractSalesPriceOutput}
              contractNetbackPriceOutput={contractNetbackPriceOutput}
              contractMarginPriceOutput={contractMarginPriceOutput}
              baseSalesPriceOutput={baseSalesPriceOutput}
              baseNetbackPriceOutput={baseNetbackPriceOutput}
              baseMarginPriceOutput={baseMarginPriceOutput}
              mainBatnaSalesPriceOutput={mainBatnaSalesPriceOutput}
              mainBatnaNetbackPriceOutput={mainBatnaNetbackPriceOutput}
              mainBatnaMarginPriceOutput={mainBatnaMarginPriceOutput}
              floorValueOutput={floorValueOutput}
              capValueOutput={capValueOutput}
              pullValueOutput={pullValueOutput}
              pushValueOutput={pushValueOutput}
              earlyTermValueOutput={earlyTermValueOutput}
              extensionValueOutput={extensionValueOutput}
              costToServeOutput={costToServeOutput}
              creditValueOutput={creditValueOutput}
              productSummary={productSummary}
              loading={loading}
            />
          ) : (
            <ChartedContainer>
              <Visuals productSummary={productSummary} view={view} productItemId={productItemId} />
            </ChartedContainer>
          )}
        </Bottom>
      )}
    </Container>
  );
};
